import { useState } from 'react'
import { Flex, Text, TextField, Select, Checkbox } from '@radix-ui/themes'
import {
  getExtraTicketProperties,
  getFieldLabel,
  isTicketFieldRequired,
} from './claim-form-schema-utils'
import { formatValue } from './claim-form-utils'
import { FieldRow } from './claim-form-fields'

/** Where a ticket's own fields live in the schema, for label lookups. */
const TICKET_FIELD_PATH = ['tickets', 'items']

type ExtraTicketFieldsProps = {
  ticket: Record<string, unknown>
  schema?: Record<string, unknown>
  onChange: (fieldKey: string, value: unknown) => void
}

type ExtraTicketFieldsReadOnlyProps = {
  ticket: Record<string, unknown>
  schema?: Record<string, unknown>
}

function fieldLabel(fieldKey: string, schema?: Record<string, unknown>): string {
  return getFieldLabel(fieldKey, schema, TICKET_FIELD_PATH)
}

/**
 * An unanswered boolean shows as indeterminate rather than unchecked. The two are different to the
 * received gate — absent counts as missing, `false` counts as answered — so rendering them alike
 * would leave an agent staring at a box that looks answered while Submit stays disabled.
 */
function booleanCheckedState(value: unknown): boolean | 'indeterminate' {
  if (typeof value === 'boolean') {
    return value
  }
  return 'indeterminate'
}

/**
 * Keeps only what can build a number, so a partly-typed value survives re-render. Deliberately
 * excludes a leading minus: no claim form quantity is negative today, and allowing it would need a
 * schema `minimum` to validate against.
 */
function sanitizeNumericInput(raw: string, allowDecimal: boolean): string {
  const stripped = raw.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '')
  const firstDot = stripped.indexOf('.')
  if (!allowDecimal || firstDot === -1) {
    return stripped
  }
  return stripped.slice(0, firstDot + 1) + stripped.slice(firstDot + 1).replace(/\./g, '')
}

function labelWithMarker(
  fieldKey: string,
  property: Record<string, unknown>,
  schema?: Record<string, unknown>,
): string {
  if (isTicketFieldRequired(fieldKey, property, schema)) {
    return `${fieldLabel(fieldKey, schema)} *`
  }
  return fieldLabel(fieldKey, schema)
}

/**
 * Editable inputs for ticket properties the drawer does not lay out explicitly — today the
 * agent-entered Aurora ID. Control type comes from the JSON Schema, so a new customer field needs no
 * change here.
 */
export function ExtraTicketFields({ ticket, schema, onChange }: ExtraTicketFieldsProps) {
  // Typed text for numeric fields, which cannot round-trip through the emitted number ("1." would
  // come back as "1"). Reset by remounting — the parent keys this component on the ticket index.
  const [numericDrafts, setNumericDrafts] = useState<Record<string, string>>({})
  const extras = getExtraTicketProperties(schema)
  if (extras.length === 0) {
    return null
  }

  return (
    <>
      {extras.map(([fieldKey, property]) => {
        const enumValues = property.enum as string[] | undefined
        const label = labelWithMarker(fieldKey, property, schema)

        if (enumValues && enumValues.length > 0) {
          return (
            <Flex
              key={fieldKey}
              direction="column"
              gap="1"
              className="claim-form-field claim-form-extra-field">
              <Text size="2" color="gray">
                {label}
              </Text>
              <Select.Root
                size="2"
                value={String(ticket[fieldKey] ?? '')}
                onValueChange={(value) => onChange(fieldKey, value)}>
                <Select.Trigger placeholder="Select..." />
                <Select.Content>
                  {enumValues.map((option) => (
                    <Select.Item key={option} value={option}>
                      {option}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>
          )
        }

        if (property.type === 'boolean') {
          return (
            // A label element, not a sibling Text: a Radix Checkbox renders a button with no text
            // content, so without this it has no accessible name and its caption is not clickable.
            <label key={fieldKey} className="claim-form-field claim-form-extra-field">
              <Flex align="center" gap="2">
                <Checkbox
                  checked={booleanCheckedState(ticket[fieldKey])}
                  onCheckedChange={(checked) => onChange(fieldKey, checked === true)}
                />
                <Text size="2" color="gray">
                  {label}
                </Text>
              </Flex>
            </label>
          )
        }

        if (property.type === 'number' || property.type === 'integer') {
          return (
            <Flex
              key={fieldKey}
              direction="column"
              gap="1"
              className="claim-form-field claim-form-extra-field">
              <Text size="2" color="gray">
                {label}
              </Text>
              {/*
                Text rather than type="number": a native number input reports an empty value for a
                partly-typed decimal like "1.", so the keystroke would be erased and a decimal could
                never be entered. The draft holds the typed text while the emitted value stays a
                number the schema accepts.
               */}
              <TextField.Root
                size="2"
                type="text"
                inputMode={property.type === 'integer' ? 'numeric' : 'decimal'}
                value={numericDrafts[fieldKey] ?? String(ticket[fieldKey] ?? '')}
                onChange={(e) => {
                  const text = sanitizeNumericInput(e.target.value, property.type === 'number')
                  setNumericDrafts((prev) => ({ ...prev, [fieldKey]: text }))
                  const parsed = Number(text)
                  if (text === '' || !Number.isFinite(parsed)) {
                    onChange(fieldKey, undefined)
                  } else {
                    onChange(fieldKey, parsed)
                  }
                }}
              />
            </Flex>
          )
        }

        return (
          <Flex
            key={fieldKey}
            direction="column"
            gap="1"
            className="claim-form-field claim-form-extra-field">
            <Text size="2" color="gray">
              {label}
            </Text>
            <TextField.Root
              size="2"
              value={String(ticket[fieldKey] ?? '')}
              maxLength={property.maxLength as number | undefined}
              onChange={(e) => {
                // Cleared or blanked fields drop the key rather than storing "", which the schema
                // rejects (these properties are optional but min-length 1) — otherwise clearing a
                // typo would fail the whole section's save.
                if (e.target.value.trim() === '') {
                  onChange(fieldKey, undefined)
                } else {
                  onChange(fieldKey, e.target.value)
                }
              }}
            />
          </Flex>
        )
      })}
    </>
  )
}

/** Locked-view counterpart of ExtraTicketFields. */
export function ExtraTicketFieldsReadOnly({ ticket, schema }: ExtraTicketFieldsReadOnlyProps) {
  const extras = getExtraTicketProperties(schema)
  if (extras.length === 0) {
    return null
  }

  return (
    <>
      {extras.map(([fieldKey]) => (
        <FieldRow
          key={fieldKey}
          className="claim-form-extra-field"
          label={fieldLabel(fieldKey, schema)}
          value={formatValue(ticket?.[fieldKey])}
        />
      ))}
    </>
  )
}
